import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Spin, Tabs } from "antd";
import axios from "axios";

import SummaryPane from "./SummaryPane";
import NetworkPane from "./NetworkPane";
import ActivityPane from "./ActivityPane";
import { useAuth } from "../../hooks/useAuth";
import { handleError, uppercaseFirst } from "../../helpers";
import { SERVICE_LEVEL } from "../../constants";
import tokens from "../../styles/tokens";
import StatusPill from "../Table/StatusPill";

const { TabPane } = Tabs;

const CLUSTER_PAGE_MAX_WIDTH = 1200;

const SIZE_LABELS = {
  XS: "Extra Small",
  S: "Small",
  M: "Medium",
  L: "Large",
};

/* ============================================================================
 * PRESERVED:backup-tab — disabled in feature/brand-refresh-foundation.
 *
 * The dedicated-cluster "Backup" tab was removed from this Tabs container as
 * part of the brand refresh. The backend is untouched: POST /clusters/:id/backup
 * and POST /clusters/:id/restore are still served by functions/src/api.ts, and
 * the BackupPane component itself still lives at ./BackupPane (it has its own
 * preservation block for the dormant bulk-restore / per-row delete sub-controls).
 *
 * Last working implementation: master tip 0e55014.
 *
 * Restoration steps:
 *   1. Re-add IMPORT below to the import block above.
 *   2. Re-add JSX (the <TabPane tab="Backup">) inside the <Tabs> tree, between
 *      the Network and Activity TabPanes — order matters for tab UX.
 *   3. Adapt to the new design system before re-enabling:
 *        - The old layout wrapped each TabPane child in
 *          <div style={{ padding: "0 20px" }}>. The refreshed layout drops that
 *          wrapper (see Summary/Network/Activity above) — keep BackupPane
 *          consistent and let the pane handle its own padding.
 *        - Verify BackupPane reads cleanly on the new dark surface tokens; if
 *          legacy Antd tag colors leak through, swap them for StatusPill (the
 *          pane already imports it).
 *   4. Once re-enabled, you can remove this preservation block or keep it as
 *      historical documentation of the brand-refresh hand-off.
 *
 * Reference (full original file):
 *   git show 0e55014:client/src/components/Cluster/Cluster.js
 * Tracking ticket: KUI-XXXX (TODO: replace with real Jira ID).
 *
 * ── IMPORTS ─────────────────────────────────────────────────────────────────
 *
 *   import BackupPane from "./BackupPane";
 *
 * ── JSX (place between Network and Activity TabPanes) ───────────────────────
 *
 *   {isDedicated && (
 *     <TabPane tab="Backup" key="backup">
 *       <BackupPane cluster={cluster} />
 *     </TabPane>
 *   )}
 * ============================================================================
 */

const Cluster = () => {
  const auth = useAuth();
  const { cluster_id } = useParams();

  const [cluster, setCluster] = useState(null);
  const [assignment, setAssignment] = useState(null);

  const fetchCluster = useCallback(() => {
    const load = async () => {
      try {
        const token = await auth.getIdToken();
        const resp1 = await axios.get(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters/${cluster_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const { cluster } = resp1.data;
        setCluster(cluster);

        const resp2 = await axios.get(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/assignments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const assignments = resp2?.data?.assignments.filter(
          (assignment) => Object.keys(assignment).length > 1
        );

        const assignment = assignments.find(
          (assignment) => assignment.cluster_id === cluster.id
        );
        if (assignment) {
          setAssignment(assignment);
        } else {
          throw Error("No assignment found for cluster");
        }
      } catch (error) {
        handleError(error);
        setCluster(null);
        setAssignment(null);
      }
    };
    load();
  }, [cluster_id, auth]);

  useEffect(() => {
    fetchCluster();
  }, [fetchCluster]);

  const headerMeta = useMemo(() => {
    if (!cluster) return null;
    const status = (cluster.status || "unknown").toLowerCase();
    const isOnline = status === "online";
    const isPending = status === "pending";
    const dotColor = isOnline
      ? tokens.semantic.success
      : isPending
      ? tokens.brand[400]
      : tokens.semantic.danger;
    const pillModifier = isOnline
      ? "online"
      : isPending
      ? "processing"
      : "error";
    return { status, isOnline, isPending, dotColor, pillModifier };
  }, [cluster]);

  if (!cluster || !assignment) {
    return (
      <div style={{ padding: 200, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const title = cluster.cluster_label || cluster.cluster_name;
  const isDedicated = cluster.service_level === SERVICE_LEVEL.DEDICATED;
  const subtitleParts = [
    cluster.cluster_name,
    isDedicated && cluster.current_version,
    isDedicated &&
      cluster.cluster_size &&
      `${SIZE_LABELS[cluster.cluster_size] || cluster.cluster_size}`,
    isDedicated && cluster.cluster_type,
  ].filter(Boolean);

  return (
    <div
      className="cluster-detail-page"
      style={{ maxWidth: CLUSTER_PAGE_MAX_WIDTH }}
    >
      <div className="cluster-detail-page__header">
        <span
          className={`status-dot${
            headerMeta.isPending ? " status-dot--pulse" : ""
          }`}
          style={{
            background: headerMeta.dotColor,
            boxShadow: `0 0 8px ${headerMeta.dotColor}`,
            width: 10,
            height: 10,
          }}
        />
        <h1 className="cluster-detail-page__title">{title}</h1>
        {!headerMeta.isOnline && (
          <StatusPill modifier={headerMeta.pillModifier}>
            {uppercaseFirst(headerMeta.status)}
          </StatusPill>
        )}
      </div>
      <div className="cluster-detail-page__subtitle">
        {subtitleParts.join("  ·  ")}
      </div>

      <Tabs size="large">
        <TabPane tab="Summary" key="summary">
          <SummaryPane cluster={cluster} assignment={assignment} />
        </TabPane>
        {isDedicated && (
          <TabPane tab="Network" key="network">
            <NetworkPane cluster={cluster} fetchCluster={fetchCluster} />
          </TabPane>
        )}
        {/* PRESERVED:backup-tab — Backup TabPane rendered here pre-brand-refresh.
            See the PRESERVED:backup-tab block at the top of this file. */}
        {isDedicated && (
          <TabPane tab="Activity" key="activity">
            <ActivityPane cluster={cluster} />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default Cluster;
